/// <reference lib="dom" />
// ^ The DOM lib is deliberately absent from tsconfig (`@react-native/typescript-config`
//   lists only ES libs) so native code cannot reach for `window` and have it type-check.
//   A `.web.ts` file is the one place those globals genuinely exist, so it opts in here
//   rather than the whole project opting in for it.

/**
 * PWA runtime — WEB build. Three browser affordances the app needs a handle on, behind
 * the same `usePwa()` shape `pwa.ts` gives native (where all three are inert).
 *
 * 1. **Registering the worker.** `/sw.js` at the origin root, so its scope is the whole
 *    app. Registration waits for `load`: a service worker install competes for
 *    bandwidth with the very bundle the user is waiting on, and on a cold first visit
 *    that trade is backwards.
 *
 * 2. **Updates, offered rather than imposed.** The worker deliberately does NOT
 *    `skipWaiting()` on install (see web/public/sw.js) — swapping the bundle under
 *    someone mid-form is how a half-typed personnel record disappears. Instead the new
 *    worker waits, this module notices, and the app shows a banner. `applyUpdate()` is
 *    the user saying yes: post SKIP_WAITING, wait for `controllerchange`, reload once.
 *
 * 3. **Install.** Chromium fires `beforeinstallprompt` and lets us defer it, so the
 *    app can put "Установить" somewhere sensible instead of accepting the browser's own
 *    mini-infobar. iOS Safari fires nothing at all and has no programmatic install —
 *    `needsManualInstall` is how the UI knows to describe «Поделиться → На экран Домой»
 *    rather than draw a button that cannot work.
 *
 * 4. **Asking again.** The offer is meant to come back on every launch until the app is
 *    actually installed, and to come back again if it is later uninstalled. Both fall
 *    out of the browser's own signals rather than from a counter we keep:
 *    `beforeinstallprompt` fires on every load while the app is installable and stops
 *    once it is installed — so "is it installed?" is a question we never have to store
 *    the answer to, and an uninstall re-arms the offer by itself. What IS stored is the
 *    「не сейчас」, and only in `sessionStorage`: it silences one launch, never the next.
 *
 * State lives in a module-level store rather than component state because all three
 * events fire once, at unpredictable times, usually before the component that cares is
 * mounted. `useSyncExternalStore` is the read side.
 */
import {useCallback, useSyncExternalStore} from 'react';

/** The slice of `BeforeInstallPromptEvent` we use; it is not in lib.dom yet. */
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>;
};

type Snapshot = {
  updateReady: boolean;
  canInstall: boolean;
  isStandalone: boolean;
  needsManualInstall: boolean;
  /** The user waved the offer away — for THIS launch only. See `dismissInstall`. */
  installDismissed: boolean;
};

/**
 * `sessionStorage`, deliberately, and not `localStorage`.
 *
 * A 「не сейчас」 that outlived the session would be indistinguishable from "never ask
 * again" — and the whole point of this offer is that it comes back. A session ends when
 * the tab is closed or the standalone app is killed, so the next launch asks once more;
 * a reload inside the same session does not, because that is the same visit and being
 * asked twice on one visit is nagging rather than offering.
 *
 * Private-mode Safari throws on write. Losing the flag costs one extra sheet later in
 * the same session; refusing to dismiss because storage said no would cost the user the
 * screen they were trying to reach.
 *
 * ⚠️ This pair sits ABOVE the `snapshot` initialiser below and must stay there. The
 * initialiser calls `readDismissed()` at module-evaluation time, and a `const` declared
 * further down is in its temporal dead zone at that moment: the read throws
 * ReferenceError, the catch swallows it, and every launch decides it was never
 * dismissed. It looks like it works — the bug only shows on the SECOND load.
 */
const DISMISS_KEY = 'samvision.install-offer-dismissed';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

const listeners = new Set<() => void>();

let deferredPrompt: InstallPromptEvent | null = null;
let waitingWorker: ServiceWorker | null = null;
let registration: ServiceWorkerRegistration | null = null;
/** Guards against a double reload if the user taps the update banner twice. */
let reloading = false;

let snapshot: Snapshot = {
  updateReady: false,
  canInstall: false,
  isStandalone: detectStandalone(),
  needsManualInstall: detectNeedsManualInstall(),
  installDismissed: readDismissed(),
};

function emit(next: Partial<Snapshot>): void {
  // useSyncExternalStore compares by identity and will loop forever on a fresh object
  // that is equal to the old one — so only publish when something actually moved.
  const merged = {...snapshot, ...next};
  if (
    merged.updateReady === snapshot.updateReady &&
    merged.canInstall === snapshot.canInstall &&
    merged.isStandalone === snapshot.isStandalone &&
    merged.needsManualInstall === snapshot.needsManualInstall &&
    merged.installDismissed === snapshot.installDismissed
  ) {
    return;
  }
  snapshot = merged;
  listeners.forEach(l => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): Snapshot {
  return snapshot;
}

/**
 * "Installed" has three different tells and no browser implements all of them:
 * Chromium/Firefox answer the display-mode media query, iOS Safari sets a non-standard
 * `navigator.standalone`, and a desktop PWA launched from a shortcut reports
 * `window-controls-overlay` or `minimal-ui` rather than `standalone`.
 */
function detectStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const modes = ['standalone', 'minimal-ui', 'fullscreen', 'window-controls-overlay'];
  const byMedia = modes.some(
    mode => window.matchMedia?.(`(display-mode: ${mode})`).matches === true,
  );
  const iosStandalone =
    (window.navigator as Navigator & {standalone?: boolean}).standalone === true;
  return byMedia || iosStandalone;
}

/**
 * iOS/iPadOS Safari — the one platform that supports installing a PWA but exposes no
 * API to ask for it. Detected by the touch-capable Apple UA rather than by feature,
 * because the missing feature (`beforeinstallprompt`) is also missing in browsers where
 * installing is impossible, and telling a Firefox-Android user to use a Share menu that
 * has no such item is worse than saying nothing.
 */
function detectNeedsManualInstall(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ claims to be a Mac; the touch points are what give it away.
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  // Every iOS browser is Safari underneath, but only Safari's share sheet has
  // "На экран «Домой»".
  const isSafari = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos && isSafari && !detectStandalone();
}

function watchWaiting(worker: ServiceWorker | null): void {
  if (!worker) {
    return;
  }
  waitingWorker = worker;
  emit({updateReady: true});
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Installability is not the service worker's business, and these two listeners used
  // to sit below the __DEV__ return that skips registration — so on a dev machine the
  // install offer could never be exercised at all. They attach first, unconditionally.
  window.addEventListener('beforeinstallprompt', event => {
    // Suppress Chromium's own mini-infobar; the app offers install in its own UI.
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    // The event firing at all means the app is NOT installed right now — which is
    // exactly the state an earlier install, since undone, would leave. Nothing has to
    // detect the uninstall; this is the detection.
    emit({canInstall: true, isStandalone: detectStandalone()});
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit({canInstall: false, isStandalone: true, needsManualInstall: false});
  });

  if (!('serviceWorker' in navigator)) {
    return;
  }

  // Installing a worker in `npm run web` would serve yesterday's dev bundle from
  // cache and make every subsequent edit look like it did not apply. Any worker
  // already registered from a production visit on the same origin is torn down, so
  // switching a dev machine between the two does not leave a stale one behind.
  if (__DEV__) {
    void navigator.serviceWorker
      .getRegistrations()
      .then(regs => regs.forEach(reg => void reg.unregister()));
    return;
  }

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js')
      .then(reg => {
        registration = reg;

        // Already waiting when we arrived — a tab was open when the deploy landed.
        watchWaiting(reg.waiting);

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) {
            return;
          }
          installing.addEventListener('statechange', () => {
            // `controller` is null on the very first install — that is not an
            // update, it is this page becoming offline-capable for the first time,
            // and prompting to reload for it would be nonsense.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              watchWaiting(installing);
            }
          });
        });
      })
      .catch(() => {
        // A failed registration costs the offline shell and nothing else; the app
        // works exactly as it did before this file existed.
      });
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) {
      return;
    }
    reloading = true;
    window.location.reload();
  });

  // A tab left open for days would otherwise never learn about a deploy. Coming back
  // to the tab is the natural moment to look, and it costs one conditional request.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void registration?.update().catch(() => undefined);
    }
  });
}

export type PwaState = {
  updateReady: boolean;
  applyUpdate: () => void;
  canInstall: boolean;
  promptInstall: () => Promise<boolean>;
  isStandalone: boolean;
  needsManualInstall: boolean;
  installDismissed: boolean;
  dismissInstall: () => void;
};

export function usePwa(): PwaState {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const applyUpdate = useCallback(() => {
    if (!waitingWorker) {
      // Nothing waiting but the banner is up (worker was replaced under us): a plain
      // reload still lands on the new bundle, because index.html is never cached.
      window.location.reload();
      return;
    }
    waitingWorker.postMessage({type: 'SKIP_WAITING'});
    // The reload itself happens in the `controllerchange` handler above, once the new
    // worker has actually taken control — reloading before that just re-runs the old
    // bundle and the banner comes straight back.
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return false;
    }
    const event = deferredPrompt;
    // The captured event is single-use; drop it before awaiting so a double-tap
    // cannot call prompt() twice (which throws).
    deferredPrompt = null;
    emit({canInstall: false});
    await event.prompt();
    const {outcome} = await event.userChoice;
    return outcome === 'accepted';
  }, []);

  const dismissInstall = useCallback(() => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // See the note on DISMISS_KEY: the sheet still closes.
    }
    emit({installDismissed: true});
  }, []);

  return {...state, applyUpdate, promptInstall, dismissInstall};
}
